import * as React from 'react';
import { useMemo } from 'react';

import { createDocumentRoute, getWorkflowRoute } from '@aline/common';
import { link } from '@aline/routes';
import { Box, IconButton, Skeleton, Typography } from '@mui/material';
import clsx from 'clsx';
import copy from 'copy-to-clipboard';
import {
  BookOpenText,
  Building2,
  ChartColumnBig,
  CheckSquare,
  ClipboardList,
  Copy,
  LayoutList,
  Mail,
  Sparkle,
  UserRound,
  Workflow,
} from 'lucide-react';
import type { PlateElementProps } from 'platejs/react';
import { PlateElement, useReadOnly, useSelected } from 'platejs/react';
import { AlineTooltip, StorableIcon } from 'src/components';
import { useThreadId } from 'src/components/dashboard/use-thread-id';
import { PulseIcon, PulseIconVariant } from 'src/components/icons/Pulse';
import { StandardLockedIcon } from 'src/components/icons/StandardLockedIcon';
import { WarningIcon } from 'src/components/icons/WarningIcon';
import {
  getOCRStatusFromFileVersion,
  isOcrProcessing,
  OCRStatus,
} from 'src/editor/toolbars/right-panel/ai/shared/OCRStatusOrChildren';
import { Apollo } from 'src/gql/generated';
import { openLink, useMaybeDocumentParams } from 'src/helpers';
import { useNotificationDispatch, useTextTruncated } from 'src/hooks';
import { NotificationsActionType } from 'src/notifications';
import { makeStyles } from 'tss-react/mui';
import { v4 as uuid4 } from 'uuid';

import { countSelectedWords } from './countSelectedWords';
import { createDocumentMentionMarkdown } from './document-mention-markdown';
import { ACCESS_STATUS_COLORS, ACCESS_STATUS_TOOLTIPS } from './MentionAccess';
import type {
  DocOrSelectionMentionElement,
  EmailMentionElement,
  IntakeFormMentionElement,
  PlaybookMentionElement,
  PlaybookRuleMentionElement,
  ReportMentionElement,
  TaskMentionElement,
  TMentionElement,
  UserMentionElement,
  WorkflowMentionElement,
} from './utils';
import type {
  DocumentMentionAccessResult,
  MentionAccessError,
  PlaybookMentionEntity,
  PlaybookRuleMentionEntity,
  ReportMentionEntity,
  TaskMentionEntity,
  WorkflowMentionEntity,
} from '../../../chat-hooks/useMentionAccessContext';
import {
  useAccessRequestStatusContext,
  useDocMentionAccessContext,
  usePlaybookMentionEntity,
  usePlaybookRuleMentionEntity,
  useReportMentionEntity,
  useTaskMentionEntity,
  useWorkflowMentionEntity,
} from '../../../chat-hooks/useMentionAccessContext';
import { isMentionAccessError } from '../../../chat-hooks/useMentionEntitiesQuery';
import { createAIChatItem } from '../../../ChatPanelMarkdown/helpers/aiChatItem';
import { ChipRemoveIcon, getChipRemoveButtonStyles, getEditorChipStyles } from '../utils';

export const CHAT_EDITOR_DATA_ATTRS = {
  documentId: 'data-document-id',
  documentName: 'data-document-name',
  mentionTypeName: 'data-mention-type-name',
  slateEditor: 'data-slate-editor',
  slateMention: 'data-slate-mention',
  slateValue: 'data-slate-value',
} as const;

const iconContainerStyle = {
  alignItems: 'center',
  display: 'inline-flex',
  flexShrink: 0,
  height: 16,
  justifyContent: 'center',
  width: 16,
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars, unused-imports/no-unused-vars -- referenced only via `typeof` to derive a type
const DOCUMENT_ICON_STATUS = {
  ACCESS_REQUESTED: 'access-requested',
  DELETED: 'deleted',
  FAILED: 'failed',
  LOADING: 'loading',
  NO_ACCESS: 'no-access',
  NONE: 'none',
  PROCESSING: 'processing',
  READY: 'ready',
} as const;

type DocumentIconStatus = (typeof DOCUMENT_ICON_STATUS)[keyof typeof DOCUMENT_ICON_STATUS];

/** Document type from the union result when accessible */
export type AccessibleDocument = Extract<DocumentMentionAccessResult, { __typename: 'Document' }>;
/** Pure function to compute document access data from the union result */
export function computeDocumentAccessData(accessResult: DocumentMentionAccessResult | null) {
  // No result yet - still loading
  if (!accessResult) {
    return {
      document: null,
      errorReason: null,
      isProcessing: false,
      loading: true,
      ocrStatus: OCRStatus.Finished,
    };
  }

  // Error result (no access or deleted)
  if (isMentionAccessError(accessResult)) {
    return {
      document: null,
      errorReason: accessResult.reason,
      isProcessing: false,
      loading: false,
      ocrStatus: OCRStatus.Finished,
    };
  }

  // Accessible document - compute processing status from latestVersion
  const computedOcrStatus = accessResult.latestVersion
    ? getOCRStatusFromFileVersion(accessResult.latestVersion)
    : OCRStatus.Finished;

  return {
    document: accessResult,
    errorReason: null,
    isProcessing: accessResult.isSavePending || isOcrProcessing(computedOcrStatus),
    loading: false,
    ocrStatus: computedOcrStatus,
  };
}

interface DocumentMentionIconProps {
  status: DocumentIconStatus;
  /** Document object for rendering the correct file type icon when ready */
  document?: AccessibleDocument | null;
}

const DocumentMentionIcon = React.memo(function DocumentMentionIcon({
  document,
  status,
}: DocumentMentionIconProps): JSX.Element | null {
  switch (status) {
    case 'no-access':
      return (
        <Box
          component="span"
          sx={{
            ...iconContainerStyle,
          }}
        >
          <StandardLockedIcon height={16} stroke={ACCESS_STATUS_COLORS.noAccess} width={16} />
        </Box>
      );

    case 'access-requested':
      return (
        <Box
          component="span"
          sx={{
            ...iconContainerStyle,
          }}
        >
          <StandardLockedIcon
            height={16}
            stroke={ACCESS_STATUS_COLORS.accessRequested}
            width={16}
          />
        </Box>
      );

    case 'deleted':
      return (
        <Box component="span" sx={iconContainerStyle}>
          <WarningIcon fill={ACCESS_STATUS_COLORS.deleted} style={{ height: 16, width: 16 }} />
        </Box>
      );

    case 'loading':
      return (
        <Box component="span" sx={iconContainerStyle}>
          <Skeleton height={16} variant="circular" width={16} />
        </Box>
      );

    case 'processing':
      return (
        <Box component="span" sx={iconContainerStyle}>
          <PulseIcon variant={PulseIconVariant.LoadingSecondary} />
        </Box>
      );

    case 'failed':
      return (
        <Box component="span" sx={iconContainerStyle}>
          <PulseIcon animate={false} variant={PulseIconVariant.Error} />
        </Box>
      );

    case 'ready':
      if (!document) return null;

      return <StorableIcon iconProps={{ height: 16, width: 16 }} storable={document} />;

    case 'none':
    default:
      return null;
  }
});

const useMentionElementStyles = makeStyles()(theme => ({
  chipCopyButton: {
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
    height: 16,
    padding: 2,
    width: 16,
  },
  mentionElement: {
    ...getEditorChipStyles(theme),
  },

  // Access requested state - gray border (text uses default color)
  mentionElementAccessRequested: {
    '&:hover': {
      borderColor: ACCESS_STATUS_COLORS.accessRequestedHover,
    },
    borderColor: ACCESS_STATUS_COLORS.accessRequested,
  },

  // Deleted state - red border (text uses default color)
  mentionElementDeleted: {
    '&:hover': {
      borderColor: ACCESS_STATUS_COLORS.deletedHover,
    },
    borderColor: ACCESS_STATUS_COLORS.deletedBorder,
  },

  // No access state - warning border (text uses default color)
  mentionElementNoAccess: {
    '&:hover': {
      borderColor: ACCESS_STATUS_COLORS.noAccessHover,
    },
    borderColor: ACCESS_STATUS_COLORS.noAccess,
  },

  // Selected state (blue border)
  mentionElementSelected: {
    borderColor: theme.palette.primary.main,
  },

  // Shared remove button style
  ...getChipRemoveButtonStyles(),
}));

export interface DocumentMentionChipProps {
  accessRequested: boolean;
  displayText: string;
  document: AccessibleDocument | null;
  errorReason: Apollo.MentionAccessErrorReason | null;
  isProcessing: boolean;
  loading: boolean;
  ocrStatus: OCRStatus;
  children?: React.ReactNode;
  isPlaceholder?: boolean;
  onMouseDown?: React.MouseEventHandler<HTMLSpanElement>;
  onOpen?: React.MouseEventHandler<HTMLSpanElement>;
  onRemove?: React.MouseEventHandler<HTMLSpanElement>;
  readOnly?: boolean;
  selected?: boolean;
}

export const DocumentMentionChip = React.memo(function DocumentMentionChip({
  accessRequested,
  children,
  displayText,
  document,
  errorReason,
  isPlaceholder = false,
  isProcessing,
  loading,
  ocrStatus,
  onMouseDown,
  onOpen,
  onRemove,
  readOnly = false,
  selected = false,
}: DocumentMentionChipProps): JSX.Element {
  const dispatch = useNotificationDispatch();
  const { classes } = useMentionElementStyles();
  const { isTruncated, ref: textRef } = useTextTruncated<HTMLSpanElement>();
  const { iconStatus, tooltipTitle } = getDocumentStatusAndTooltip({
    accessRequested,
    document,
    errorReason,
    isPlaceholder,
    isProcessing,
    loading,
    ocrStatus,
    readOnly,
  });

  const mentionTooltipContent = tooltipTitle || (isTruncated ? displayText : '');
  const mentionClassName = clsx(
    classes.mentionElement,
    selected && classes.mentionElementSelected,
    errorReason === Apollo.MentionAccessErrorReason.NoAccess &&
      (accessRequested ? classes.mentionElementAccessRequested : classes.mentionElementNoAccess),
    errorReason === Apollo.MentionAccessErrorReason.Deleted && classes.mentionElementDeleted
  );
  const handleCopyMention = React.useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      if (!document) return;

      event.preventDefault();
      event.stopPropagation();
      copy(createDocumentMentionMarkdown({ displayText, documentId: document.id }));
      dispatch({
        payload: {
          key: uuid4(),
          message: 'Document mention copied to clipboard.',
          variant: 'success',
        },
        type: NotificationsActionType.PUSH_NOTIFICATION,
      });
    },
    [dispatch, displayText, document]
  );
  const stopCopyMouseDown = React.useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
  }, []);

  return (
    <AlineTooltip arrow placement="top" title={mentionTooltipContent}>
      <Box className={mentionClassName} component="span" onClick={onOpen} onMouseDown={onMouseDown}>
        {children}
        <DocumentMentionIcon document={document} status={iconStatus} />
        <Typography ref={textRef} noWrap component="span">
          {displayText}
        </Typography>
        {!readOnly && onRemove && (
          <span className={classes.chipRemoveButton} onClick={onRemove} onMouseDown={onMouseDown}>
            <ChipRemoveIcon />
          </span>
        )}
        {readOnly && document && (
          <AlineTooltip arrow placement="top" title="Copy to clipboard">
            <IconButton
              className={classes.chipCopyButton}
              size="small"
              onClick={handleCopyMention}
              onMouseDown={stopCopyMouseDown}
            >
              <Copy size={12} strokeWidth={2} />
            </IconButton>
          </AlineTooltip>
        )}
      </Box>
    </AlineTooltip>
  );
});

export const EntityMentionChip = React.memo(function EntityMentionChip({
  children,
  displayText,
  errorReason = null,
  icon,
  onMouseDown,
  onOpen,
  onRemove,
  readOnly = false,
  selected = false,
}: {
  displayText: string;
  icon: React.ReactNode;
  children?: React.ReactNode;
  errorReason?: Apollo.MentionAccessErrorReason | null;
  onMouseDown?: React.MouseEventHandler<HTMLSpanElement>;
  onOpen?: React.MouseEventHandler<HTMLSpanElement>;
  onRemove?: React.MouseEventHandler<HTMLSpanElement>;
  readOnly?: boolean;
  selected?: boolean;
}): JSX.Element {
  const { classes } = useMentionElementStyles();
  const { isTruncated, ref: textRef } = useTextTruncated<HTMLSpanElement>();
  const mentionTooltipContent =
    getEntityMentionTooltip(errorReason) || (isTruncated ? displayText : '');

  return (
    <AlineTooltip arrow placement="top" title={mentionTooltipContent}>
      <Box
        className={clsx(
          classes.mentionElement,
          selected && classes.mentionElementSelected,
          errorReason === Apollo.MentionAccessErrorReason.NoAccess &&
            classes.mentionElementNoAccess,
          errorReason === Apollo.MentionAccessErrorReason.Deleted && classes.mentionElementDeleted
        )}
        component="span"
        onClick={onOpen}
        onMouseDown={onMouseDown}
      >
        {children}
        <Box component="span" sx={iconContainerStyle}>
          {icon}
        </Box>
        <Typography ref={textRef} noWrap component="span">
          {displayText}
        </Typography>
        {!readOnly && onRemove && (
          <span className={classes.chipRemoveButton} onClick={onRemove} onMouseDown={onMouseDown}>
            <ChipRemoveIcon />
          </span>
        )}
      </Box>
    </AlineTooltip>
  );
});

function getDocumentStatusAndTooltip({
  accessRequested,
  document,
  errorReason,
  isPlaceholder,
  isProcessing,
  loading,
  ocrStatus,
  readOnly,
}: Pick<
  DocumentMentionChipProps,
  | 'accessRequested'
  | 'document'
  | 'errorReason'
  | 'isPlaceholder'
  | 'isProcessing'
  | 'loading'
  | 'ocrStatus'
  | 'readOnly'
>): {
  iconStatus: DocumentIconStatus;
  tooltipTitle: string;
} {
  if (isPlaceholder) {
    return {
      iconStatus: 'processing',
      tooltipTitle: 'Uploading document...',
    };
  }

  if (errorReason === Apollo.MentionAccessErrorReason.NoAccess) {
    return {
      iconStatus: accessRequested ? 'access-requested' : 'no-access',
      tooltipTitle: accessRequested
        ? ACCESS_STATUS_TOOLTIPS.accessRequested
        : ACCESS_STATUS_TOOLTIPS.noAccess,
    };
  }

  if (errorReason === Apollo.MentionAccessErrorReason.Deleted) {
    return {
      iconStatus: 'deleted',
      tooltipTitle: ACCESS_STATUS_TOOLTIPS.deleted,
    };
  }

  if (readOnly) return { iconStatus: document ? 'ready' : 'none', tooltipTitle: '' };

  if (loading) {
    return {
      iconStatus: 'loading',
      tooltipTitle: 'Loading document...',
    };
  }

  if (isProcessing) {
    return {
      iconStatus: 'processing',
      tooltipTitle: 'Document is being processed, must finish before you can use it in AI Chat.',
    };
  }

  if (ocrStatus === OCRStatus.Failed) {
    return {
      iconStatus: 'failed',
      tooltipTitle: 'Document processing failed. Cannot use in AI Chat.',
    };
  }

  if (document) return { iconStatus: 'ready', tooltipTitle: '' };

  return { iconStatus: 'none', tooltipTitle: '' };
}

function getEntityMentionTooltip(errorReason: Apollo.MentionAccessErrorReason | null) {
  if (errorReason === Apollo.MentionAccessErrorReason.NoAccess)
    return "You don't have access to this mention.";
  if (errorReason === Apollo.MentionAccessErrorReason.Deleted)
    return 'This mention has been deleted.';
  return '';
}

function getAccessibleTaskMentionEntity(result: TaskMentionEntity | null) {
  if (!result || isMentionAccessError(result)) return null;
  return result;
}

function getAccessibleWorkflowMentionEntity(result: WorkflowMentionEntity | null) {
  if (!result || isMentionAccessError(result)) return null;
  return result;
}

function getAccessibleReportMentionEntity(result: ReportMentionEntity | null) {
  if (!result || isMentionAccessError(result)) return null;
  return result;
}

function getAccessiblePlaybookMentionEntity(result: PlaybookMentionEntity | null) {
  if (!result || isMentionAccessError(result)) return null;
  return result;
}

function getAccessiblePlaybookRuleMentionEntity(result: PlaybookRuleMentionEntity | null) {
  if (!result || isMentionAccessError(result)) return null;
  return result;
}

function getMentionAccessError(
  result:
    | MentionAccessError
    | PlaybookMentionEntity
    | PlaybookRuleMentionEntity
    | TaskMentionEntity
    | WorkflowMentionEntity
    | ReportMentionEntity
    | null
) {
  if (!result || !isMentionAccessError(result)) return null;
  return result;
}

function notifyMentionAccessError(
  dispatch: ReturnType<typeof useNotificationDispatch>,
  entityLabel: string,
  reason: Apollo.MentionAccessErrorReason
) {
  dispatch({
    payload: {
      key: uuid4(),
      message:
        reason === Apollo.MentionAccessErrorReason.Deleted
          ? `This ${entityLabel} has been deleted and cannot be opened.`
          : `You don't have access to this ${entityLabel}.`,
      variant: 'warning',
    },
    type: NotificationsActionType.PUSH_NOTIFICATION,
  });
}

const DocumentMentionElement = React.memo(function DocumentMentionElement({
  element,
  props,
}: {
  element: DocOrSelectionMentionElement;
  props: PlateElementProps<TMentionElement> & { prefix?: string };
}): JSX.Element {
  const { editor } = props;
  const readOnly = useReadOnly();
  const selected = useSelected();
  const { threadId } = useThreadId();
  const dispatch = useNotificationDispatch();
  const { documentId: currentDocumentId } = useMaybeDocumentParams();
  const isDocumentInView = currentDocumentId === element.documentId;

  // Get access request status from server-side context
  const { isPending: accessRequested } = useAccessRequestStatusContext(element.documentId);

  /**
   * Get access result from context (provided by ChatPanel/ChatPanelInput).
   * This is either a Document (accessible) or MentionAccessError (inaccessible/deleted).
   */
  const accessResult = useDocMentionAccessContext(element.documentId);
  const { document, errorReason, isProcessing, loading, ocrStatus } = useMemo(
    () => computeDocumentAccessData(accessResult),
    [accessResult]
  );
  const selectedWordCount = countSelectedWords(element);
  const displayText =
    typeof selectedWordCount === 'number'
      ? `${element.documentName || 'Untitled document'} (${selectedWordCount} words)`
      : `${element.documentName || 'Untitled document'}`;

  const handleRemove = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    editor.tf.removeNodes({ at: editor.api.findPath(element) });
  };

  const stopMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleOpen = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    // Don't navigate for deleted documents
    if (errorReason === Apollo.MentionAccessErrorReason.Deleted) {
      dispatch({
        payload: {
          key: uuid4(),
          message: 'This document has been deleted and cannot be opened.',
          variant: 'warning',
        },
        type: NotificationsActionType.PUSH_NOTIFICATION,
      });
      return;
    }

    // Don't navigate for no-access documents
    if (errorReason === Apollo.MentionAccessErrorReason.NoAccess) {
      dispatch({
        payload: {
          key: uuid4(),
          message: "You don't have access to this document.",
          variant: 'warning',
        },
        type: NotificationsActionType.PUSH_NOTIFICATION,
      });
      return;
    }

    if (element.mentionTypeName === 'document' && element.isPlaceholder) {
      dispatch({
        payload: {
          key: uuid4(),
          message: 'Document is still uploading. Please wait for it to finish.',
          variant: 'warning',
        },
        type: NotificationsActionType.PUSH_NOTIFICATION,
      });
      return;
    }

    if (isDocumentInView) {
      dispatch({
        payload: {
          key: uuid4(),
          message: 'Document is already in view.',
          variant: 'info',
        },
        type: NotificationsActionType.PUSH_NOTIFICATION,
      });
      return;
    }

    const documentRoute = threadId
      ? createDocumentRoute(element.documentId, {
          aiChatItem: createAIChatItem({
            threadId,
            type: 'thread',
          }),
        })
      : link.DocumentDetail({ documentId: element.documentId });

    openLink(documentRoute, { title: element.documentName || 'Untitled Document' });
  };

  return (
    <PlateElement
      {...props}
      as="span"
      attributes={{
        ...props.attributes,
        contentEditable: false,
        [CHAT_EDITOR_DATA_ATTRS.documentId]: element.documentId,
        [CHAT_EDITOR_DATA_ATTRS.documentName]: element.documentName,
        [CHAT_EDITOR_DATA_ATTRS.mentionTypeName]: element.mentionTypeName,
        [CHAT_EDITOR_DATA_ATTRS.slateMention]: true,
        [CHAT_EDITOR_DATA_ATTRS.slateValue]: element.value,
      }}
    >
      <DocumentMentionChip
        accessRequested={accessRequested}
        displayText={displayText}
        document={document}
        errorReason={errorReason}
        isPlaceholder={element.mentionTypeName === 'document' && Boolean(element.isPlaceholder)}
        isProcessing={isProcessing}
        loading={loading}
        ocrStatus={ocrStatus}
        readOnly={readOnly}
        selected={selected}
        onMouseDown={stopMouseDown}
        onOpen={handleOpen}
        onRemove={handleRemove}
      >
        {props.children}
      </DocumentMentionChip>
    </PlateElement>
  );
});

const TaskMentionChipElement = React.memo(function TaskMentionChipElement({
  element,
  props,
}: {
  element: TaskMentionElement;
  props: PlateElementProps<TMentionElement> & { prefix?: string };
}): JSX.Element {
  const { editor } = props;
  const readOnly = useReadOnly();
  const selected = useSelected();
  const dispatch = useNotificationDispatch();
  const taskResult = useTaskMentionEntity(element.taskId);
  const task = getAccessibleTaskMentionEntity(taskResult);
  const accessError = getMentionAccessError(taskResult);
  const displayText = task?.title || element.taskName || element.value || 'Untitled Task';

  const handleRemove = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    editor.tf.removeNodes({ at: editor.api.findPath(element) });
  };

  const stopMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleOpen = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (accessError) {
      notifyMentionAccessError(dispatch, 'task', accessError.reason);
      return;
    }

    if (!task?.workflow.id) {
      dispatch({
        payload: {
          key: uuid4(),
          message: 'Task details are still loading.',
          variant: 'info',
        },
        type: NotificationsActionType.PUSH_NOTIFICATION,
      });
      return;
    }

    openLink(link.WorkflowDetailTabTask({ taskId: element.taskId, workflowId: task.workflow.id }), {
      title: displayText,
    });
  };

  return (
    <PlateElement
      {...props}
      as="span"
      attributes={{
        ...props.attributes,
        contentEditable: false,
        [CHAT_EDITOR_DATA_ATTRS.mentionTypeName]: element.mentionTypeName,
        [CHAT_EDITOR_DATA_ATTRS.slateMention]: true,
        [CHAT_EDITOR_DATA_ATTRS.slateValue]: element.value,
      }}
    >
      <EntityMentionChip
        displayText={displayText}
        errorReason={accessError?.reason ?? null}
        icon={<CheckSquare size={14} strokeWidth={2} />}
        readOnly={readOnly}
        selected={selected}
        onMouseDown={stopMouseDown}
        onOpen={handleOpen}
        onRemove={handleRemove}
      >
        {props.children}
      </EntityMentionChip>
    </PlateElement>
  );
});

const WorkflowMentionChipElement = React.memo(function WorkflowMentionChipElement({
  element,
  props,
}: {
  element: WorkflowMentionElement;
  props: PlateElementProps<TMentionElement> & { prefix?: string };
}): JSX.Element {
  const { editor } = props;
  const readOnly = useReadOnly();
  const selected = useSelected();
  const dispatch = useNotificationDispatch();
  const workflowResult = useWorkflowMentionEntity(element.workflowId);
  const workflow = getAccessibleWorkflowMentionEntity(workflowResult);
  const accessError = getMentionAccessError(workflowResult);
  const displayText =
    workflow?.name || element.workflowName || element.value || 'Untitled Workflow';

  const handleRemove = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    editor.tf.removeNodes({ at: editor.api.findPath(element) });
  };

  const stopMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleOpen = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (accessError) {
      notifyMentionAccessError(dispatch, 'workflow', accessError.reason);
      return;
    }

    if (!workflow) {
      dispatch({
        payload: {
          key: uuid4(),
          message: 'Workflow details are still loading.',
          variant: 'info',
        },
        type: NotificationsActionType.PUSH_NOTIFICATION,
      });
      return;
    }

    const isTemplate = workflow.variant === Apollo.WorkflowVariant.Template;
    openLink(getWorkflowRoute(element.workflowId, { isTemplate }), { title: displayText });
  };

  return (
    <PlateElement
      {...props}
      as="span"
      attributes={{
        ...props.attributes,
        contentEditable: false,
        [CHAT_EDITOR_DATA_ATTRS.mentionTypeName]: element.mentionTypeName,
        [CHAT_EDITOR_DATA_ATTRS.slateMention]: true,
        [CHAT_EDITOR_DATA_ATTRS.slateValue]: element.value,
      }}
    >
      <EntityMentionChip
        displayText={displayText}
        errorReason={accessError?.reason ?? null}
        icon={<Workflow size={14} strokeWidth={2} />}
        readOnly={readOnly}
        selected={selected}
        onMouseDown={stopMouseDown}
        onOpen={handleOpen}
        onRemove={handleRemove}
      >
        {props.children}
      </EntityMentionChip>
    </PlateElement>
  );
});

const ReportMentionChipElement = React.memo(function ReportMentionChipElement({
  element,
  props,
}: {
  element: ReportMentionElement;
  props: PlateElementProps<TMentionElement> & { prefix?: string };
}): JSX.Element {
  const { editor } = props;
  const readOnly = useReadOnly();
  const selected = useSelected();
  const dispatch = useNotificationDispatch();
  const reportResult = useReportMentionEntity(element.reportId);
  const report = getAccessibleReportMentionEntity(reportResult);
  const accessError = getMentionAccessError(reportResult);
  const displayText = report?.name || element.reportName || element.value || 'Untitled Report';

  const handleRemove = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    editor.tf.removeNodes({ at: editor.api.findPath(element) });
  };

  const stopMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleOpen = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (accessError) {
      notifyMentionAccessError(dispatch, 'report', accessError.reason);
      return;
    }

    if (!report) {
      dispatch({
        payload: {
          key: uuid4(),
          message: 'Report details are still loading.',
          variant: 'info',
        },
        type: NotificationsActionType.PUSH_NOTIFICATION,
      });
      return;
    }

    openLink(link.ReportDetail({ reportId: element.reportId }), { title: displayText });
  };

  return (
    <PlateElement
      {...props}
      as="span"
      attributes={{
        ...props.attributes,
        contentEditable: false,
        [CHAT_EDITOR_DATA_ATTRS.mentionTypeName]: element.mentionTypeName,
        [CHAT_EDITOR_DATA_ATTRS.slateMention]: true,
        [CHAT_EDITOR_DATA_ATTRS.slateValue]: element.value,
      }}
    >
      <EntityMentionChip
        displayText={displayText}
        errorReason={accessError?.reason ?? null}
        icon={<ChartColumnBig size={14} strokeWidth={2} />}
        readOnly={readOnly}
        selected={selected}
        onMouseDown={stopMouseDown}
        onOpen={handleOpen}
        onRemove={handleRemove}
      >
        {props.children}
      </EntityMentionChip>
    </PlateElement>
  );
});

const PlaybookMentionChipElement = React.memo(function PlaybookMentionChipElement({
  element,
  props,
}: {
  element: PlaybookMentionElement;
  props: PlateElementProps<TMentionElement> & { prefix?: string };
}): JSX.Element {
  const { editor } = props;
  const readOnly = useReadOnly();
  const selected = useSelected();
  const playbookResult = usePlaybookMentionEntity(element.playbookId);
  const playbook = getAccessiblePlaybookMentionEntity(playbookResult);
  const accessError = getMentionAccessError(playbookResult);
  const displayText =
    playbook?.title || element.playbookName || element.value || 'Untitled Playbook';

  const handleRemove = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    editor.tf.removeNodes({ at: editor.api.findPath(element) });
  };

  const stopMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  return (
    <PlateElement
      {...props}
      as="span"
      attributes={{
        ...props.attributes,
        contentEditable: false,
        [CHAT_EDITOR_DATA_ATTRS.mentionTypeName]: element.mentionTypeName,
        [CHAT_EDITOR_DATA_ATTRS.slateMention]: true,
        [CHAT_EDITOR_DATA_ATTRS.slateValue]: element.value,
      }}
    >
      <EntityMentionChip
        displayText={displayText}
        errorReason={accessError?.reason ?? null}
        icon={<BookOpenText size={14} strokeWidth={2} />}
        readOnly={readOnly}
        selected={selected}
        onMouseDown={stopMouseDown}
        onRemove={handleRemove}
      >
        {props.children}
      </EntityMentionChip>
    </PlateElement>
  );
});

const PlaybookRuleMentionChipElement = React.memo(function PlaybookRuleMentionChipElement({
  element,
  props,
}: {
  element: PlaybookRuleMentionElement;
  props: PlateElementProps<TMentionElement> & { prefix?: string };
}): JSX.Element {
  const { editor } = props;
  const readOnly = useReadOnly();
  const selected = useSelected();
  const ruleResult = usePlaybookRuleMentionEntity(element.playbookRuleId);
  const rule = getAccessiblePlaybookRuleMentionEntity(ruleResult);
  const accessError = getMentionAccessError(ruleResult);
  const displayText = rule?.title || element.playbookRuleName || element.value || 'Untitled rule';

  const handleRemove = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    editor.tf.removeNodes({ at: editor.api.findPath(element) });
  };

  const stopMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  return (
    <PlateElement
      {...props}
      as="span"
      attributes={{
        ...props.attributes,
        contentEditable: false,
        [CHAT_EDITOR_DATA_ATTRS.mentionTypeName]: element.mentionTypeName,
        [CHAT_EDITOR_DATA_ATTRS.slateMention]: true,
        [CHAT_EDITOR_DATA_ATTRS.slateValue]: element.value,
      }}
    >
      <EntityMentionChip
        displayText={displayText}
        errorReason={accessError?.reason ?? null}
        icon={<LayoutList size={14} strokeWidth={2} />}
        readOnly={readOnly}
        selected={selected}
        onMouseDown={stopMouseDown}
        onRemove={handleRemove}
      >
        {props.children}
      </EntityMentionChip>
    </PlateElement>
  );
});

export const ReadOnlyTaskMentionChip = React.memo(function ReadOnlyTaskMentionChip({
  fallbackDisplayText,
  taskId,
}: {
  taskId: string;
  fallbackDisplayText?: string;
}): JSX.Element {
  const dispatch = useNotificationDispatch();
  const taskResult = useTaskMentionEntity(taskId);
  const task = getAccessibleTaskMentionEntity(taskResult);
  const accessError = getMentionAccessError(taskResult);
  const displayText = task?.title || fallbackDisplayText || 'Untitled Task';

  const handleOpen = React.useCallback(
    (event: React.MouseEvent<HTMLSpanElement>) => {
      event.preventDefault();
      event.stopPropagation();

      if (accessError) {
        notifyMentionAccessError(dispatch, 'task', accessError.reason);
        return;
      }

      if (!task?.workflow.id) {
        dispatch({
          payload: {
            key: uuid4(),
            message: 'Task details are still loading.',
            variant: 'info',
          },
          type: NotificationsActionType.PUSH_NOTIFICATION,
        });
        return;
      }

      openLink(link.WorkflowDetailTabTask({ taskId, workflowId: task.workflow.id }), {
        title: displayText,
      });
    },
    [accessError, dispatch, displayText, task?.workflow.id, taskId]
  );

  return (
    <EntityMentionChip
      readOnly
      displayText={displayText}
      errorReason={accessError?.reason ?? null}
      icon={<CheckSquare size={14} strokeWidth={2} />}
      onOpen={handleOpen}
    />
  );
});

export const ReadOnlyWorkflowMentionChip = React.memo(function ReadOnlyWorkflowMentionChip({
  fallbackDisplayText,
  workflowId,
}: {
  workflowId: string;
  fallbackDisplayText?: string;
}): JSX.Element {
  const dispatch = useNotificationDispatch();
  const workflowResult = useWorkflowMentionEntity(workflowId);
  const accessError = getMentionAccessError(workflowResult);
  const workflow = getAccessibleWorkflowMentionEntity(workflowResult);
  const displayText = workflow?.name || fallbackDisplayText || 'Untitled Workflow';

  const handleOpen = React.useCallback(
    (event: React.MouseEvent<HTMLSpanElement>) => {
      event.preventDefault();
      event.stopPropagation();

      if (accessError) {
        notifyMentionAccessError(dispatch, 'workflow', accessError.reason);
        return;
      }

      if (!workflow) {
        dispatch({
          payload: {
            key: uuid4(),
            message: 'Workflow details are still loading.',
            variant: 'info',
          },
          type: NotificationsActionType.PUSH_NOTIFICATION,
        });
        return;
      }

      const isTemplate = workflow.variant === Apollo.WorkflowVariant.Template;
      openLink(getWorkflowRoute(workflowId, { isTemplate }), { title: displayText });
    },
    [accessError, dispatch, displayText, workflow, workflowId]
  );

  return (
    <EntityMentionChip
      readOnly
      displayText={displayText}
      errorReason={accessError?.reason ?? null}
      icon={<Workflow size={14} strokeWidth={2} />}
      onOpen={handleOpen}
    />
  );
});

export const ReadOnlyReportMentionChip = React.memo(function ReadOnlyReportMentionChip({
  fallbackDisplayText,
  reportId,
}: {
  reportId: string;
  fallbackDisplayText?: string;
}): JSX.Element {
  const dispatch = useNotificationDispatch();
  const reportResult = useReportMentionEntity(reportId);
  const accessError = getMentionAccessError(reportResult);
  const report = getAccessibleReportMentionEntity(reportResult);
  const displayText = report?.name || fallbackDisplayText || 'Untitled Report';

  const handleOpen = React.useCallback(
    (event: React.MouseEvent<HTMLSpanElement>) => {
      event.preventDefault();
      event.stopPropagation();

      if (accessError) {
        notifyMentionAccessError(dispatch, 'report', accessError.reason);
        return;
      }

      if (!report) {
        dispatch({
          payload: {
            key: uuid4(),
            message: 'Report details are still loading.',
            variant: 'info',
          },
          type: NotificationsActionType.PUSH_NOTIFICATION,
        });
        return;
      }

      openLink(link.ReportDetail({ reportId }), { title: displayText });
    },
    [accessError, dispatch, displayText, report, reportId]
  );

  return (
    <EntityMentionChip
      readOnly
      displayText={displayText}
      errorReason={accessError?.reason ?? null}
      icon={<ChartColumnBig size={14} strokeWidth={2} />}
      onOpen={handleOpen}
    />
  );
});

export const ReadOnlyAlineAiMentionChip = React.memo(function ReadOnlyAlineAiMentionChip({
  fallbackDisplayText,
}: {
  fallbackDisplayText?: string;
}): JSX.Element {
  return (
    <EntityMentionChip
      readOnly
      displayText={fallbackDisplayText || 'Aline AI'}
      icon={<Sparkle size={14} strokeWidth={2} />}
    />
  );
});

export const ReadOnlyEmailMentionChip = React.memo(function ReadOnlyEmailMentionChip({
  email,
  fallbackDisplayText,
}: {
  email: string;
  fallbackDisplayText?: string;
}): JSX.Element {
  return (
    <EntityMentionChip
      readOnly
      displayText={fallbackDisplayText || email}
      icon={<Mail size={14} strokeWidth={2} />}
    />
  );
});

export const ReadOnlyIntakeFormMentionChip = React.memo(function ReadOnlyIntakeFormMentionChip({
  fallbackDisplayText,
}: {
  fallbackDisplayText?: string;
}): JSX.Element {
  return (
    <EntityMentionChip
      readOnly
      displayText={fallbackDisplayText || 'Intake Form'}
      icon={<ClipboardList size={14} strokeWidth={2} />}
    />
  );
});

export const ReadOnlyPartyMentionChip = React.memo(function ReadOnlyPartyMentionChip({
  fallbackDisplayText,
  partyId,
}: {
  partyId: string;
  fallbackDisplayText?: string;
}): JSX.Element {
  const displayText = fallbackDisplayText || 'Unknown Party';

  const handleOpen = React.useCallback(
    (event: React.MouseEvent<HTMLSpanElement>) => {
      event.preventDefault();
      event.stopPropagation();

      openLink(link.PartyDetailDocuments({ partyId }), { title: displayText });
    },
    [displayText, partyId]
  );

  return (
    <EntityMentionChip
      readOnly
      displayText={displayText}
      icon={<Building2 size={14} strokeWidth={2} />}
      onOpen={handleOpen}
    />
  );
});

export const ReadOnlyPlaybookMentionChip = React.memo(function ReadOnlyPlaybookMentionChip({
  fallbackDisplayText,
  playbookId,
}: {
  playbookId: string;
  fallbackDisplayText?: string;
}): JSX.Element {
  const dispatch = useNotificationDispatch();
  const documentParams = useMaybeDocumentParams();
  const playbookResult = usePlaybookMentionEntity(playbookId);
  const playbook = getAccessiblePlaybookMentionEntity(playbookResult);
  const accessError = getMentionAccessError(playbookResult);
  const displayText = playbook?.title || fallbackDisplayText || 'Untitled Playbook';

  const handleOpen = React.useCallback(
    (event: React.MouseEvent<HTMLSpanElement>) => {
      event.preventDefault();
      event.stopPropagation();

      if (accessError) {
        notifyMentionAccessError(dispatch, 'playbook', accessError.reason);
        return;
      }

      if (documentParams?.documentId) {
        openLink(link.DocumentDetailPlaybooks({ documentId: documentParams.documentId }), {
          title: displayText,
        });
      }
    },
    [accessError, dispatch, displayText, documentParams?.documentId]
  );

  return (
    <EntityMentionChip
      readOnly
      displayText={displayText}
      errorReason={accessError?.reason ?? null}
      icon={<BookOpenText size={14} strokeWidth={2} />}
      onOpen={handleOpen}
    />
  );
});

/** Opens the rule in its playbook's page; the playbook comes from the hydrated rule. */
export const ReadOnlyPlaybookRuleMentionChip = React.memo(function ReadOnlyPlaybookRuleMentionChip({
  fallbackDisplayText,
  playbookRuleId,
}: {
  playbookRuleId: string;
  fallbackDisplayText?: string;
}): JSX.Element {
  const dispatch = useNotificationDispatch();
  const ruleResult = usePlaybookRuleMentionEntity(playbookRuleId);
  const rule = getAccessiblePlaybookRuleMentionEntity(ruleResult);
  const accessError = getMentionAccessError(ruleResult);
  const displayText = rule?.title || fallbackDisplayText || 'Untitled rule';

  const handleOpen = React.useCallback(
    (event: React.MouseEvent<HTMLSpanElement>) => {
      event.preventDefault();
      event.stopPropagation();

      if (accessError) {
        notifyMentionAccessError(dispatch, 'rule', accessError.reason);
        return;
      }

      if (!rule) {
        dispatch({
          payload: {
            key: uuid4(),
            message: 'Rule details are still loading.',
            variant: 'info',
          },
          type: NotificationsActionType.PUSH_NOTIFICATION,
        });
        return;
      }

      openLink(link.PlaybooksDetailRule({ playbookId: rule.playbook.id, ruleId: playbookRuleId }), {
        title: displayText,
      });
    },
    [accessError, dispatch, displayText, playbookRuleId, rule]
  );

  return (
    <EntityMentionChip
      readOnly
      displayText={displayText}
      errorReason={accessError?.reason ?? null}
      icon={<LayoutList size={14} strokeWidth={2} />}
      onOpen={handleOpen}
    />
  );
});

export const ReadOnlyUserMentionChip = React.memo(function ReadOnlyUserMentionChip({
  fallbackDisplayText,
}: {
  fallbackDisplayText?: string;
}): JSX.Element {
  return (
    <EntityMentionChip
      readOnly
      displayText={fallbackDisplayText || 'Unknown user'}
      icon={<UserRound size={14} strokeWidth={2} />}
    />
  );
});

/**
 * Chip for a user mention. Purely presentational (no queries): the display name is
 * persisted on the element itself, so the chip renders identically in editable and
 * read-only editors, including outside the chat providers (e.g. task descriptions).
 */
const UserMentionChipElement = React.memo(function UserMentionChipElement({
  element,
  props,
}: {
  element: UserMentionElement;
  props: PlateElementProps<TMentionElement> & { prefix?: string };
}): JSX.Element {
  const { editor } = props;
  const readOnly = useReadOnly();
  const selected = useSelected();
  const { classes } = useMentionElementStyles();

  const handleRemove = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    editor.tf.removeNodes({ at: editor.api.findPath(element) });
  };

  const stopMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  return (
    <PlateElement
      {...props}
      as="span"
      attributes={{
        ...props.attributes,
        contentEditable: false,
        [CHAT_EDITOR_DATA_ATTRS.mentionTypeName]: element.mentionTypeName,
        [CHAT_EDITOR_DATA_ATTRS.slateValue]: element.value,
      }}
    >
      <Box
        className={clsx(classes.mentionElement, selected && classes.mentionElementSelected)}
        component="span"
        onMouseDown={stopMouseDown}
      >
        {props.children}
        <Box component="span" sx={iconContainerStyle}>
          <UserRound size={14} strokeWidth={2} />
        </Box>
        <Typography noWrap component="span">
          {element.userName || element.value}
        </Typography>
        {!readOnly && (
          <span className={classes.chipRemoveButton} onClick={handleRemove}>
            <ChipRemoveIcon />
          </span>
        )}
      </Box>
    </PlateElement>
  );
});

const EmailMentionChipElement = React.memo(function EmailMentionChipElement({
  element,
  props,
}: {
  element: EmailMentionElement;
  props: PlateElementProps<TMentionElement> & { prefix?: string };
}): JSX.Element {
  const { editor } = props;
  const readOnly = useReadOnly();
  const selected = useSelected();
  const { classes } = useMentionElementStyles();

  const handleRemove = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    editor.tf.removeNodes({ at: editor.api.findPath(element) });
  };

  const stopMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  return (
    <PlateElement
      {...props}
      as="span"
      attributes={{
        ...props.attributes,
        contentEditable: false,
        [CHAT_EDITOR_DATA_ATTRS.mentionTypeName]: element.mentionTypeName,
        [CHAT_EDITOR_DATA_ATTRS.slateValue]: element.value,
      }}
    >
      <Box
        className={clsx(classes.mentionElement, selected && classes.mentionElementSelected)}
        component="span"
        onMouseDown={stopMouseDown}
      >
        {props.children}
        <Box component="span" sx={iconContainerStyle}>
          <Mail size={14} strokeWidth={2} />
        </Box>
        <Typography noWrap component="span">
          {element.email}
        </Typography>
        {!readOnly && (
          <span className={classes.chipRemoveButton} onClick={handleRemove}>
            <ChipRemoveIcon />
          </span>
        )}
      </Box>
    </PlateElement>
  );
});

const IntakeFormMentionChipElement = React.memo(function IntakeFormMentionChipElement({
  element,
  props,
}: {
  element: IntakeFormMentionElement;
  props: PlateElementProps<TMentionElement> & { prefix?: string };
}): JSX.Element {
  const { editor } = props;
  const readOnly = useReadOnly();
  const selected = useSelected();
  const { classes } = useMentionElementStyles();

  const handleRemove = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    editor.tf.removeNodes({ at: editor.api.findPath(element) });
  };

  const stopMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  return (
    <PlateElement
      {...props}
      as="span"
      attributes={{
        ...props.attributes,
        contentEditable: false,
        [CHAT_EDITOR_DATA_ATTRS.mentionTypeName]: element.mentionTypeName,
        [CHAT_EDITOR_DATA_ATTRS.slateValue]: element.value,
      }}
    >
      <Box
        className={clsx(classes.mentionElement, selected && classes.mentionElementSelected)}
        component="span"
        onMouseDown={stopMouseDown}
      >
        {props.children}
        <Box component="span" sx={iconContainerStyle}>
          <ClipboardList size={14} strokeWidth={2} />
        </Box>
        <Typography noWrap component="span">
          Intake Form
        </Typography>
        {!readOnly && (
          <span className={classes.chipRemoveButton} onClick={handleRemove}>
            <ChipRemoveIcon />
          </span>
        )}
      </Box>
    </PlateElement>
  );
});

const AlineAiMentionChipElement = React.memo(function AlineAiMentionChipElement({
  props,
}: {
  props: PlateElementProps<TMentionElement> & { prefix?: string };
}): JSX.Element {
  const { classes } = useMentionElementStyles();
  const { editor, element } = props;
  const readOnly = useReadOnly();
  const selected = useSelected();

  const handleRemove = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    editor.tf.removeNodes({ at: editor.api.findPath(element) });
  };

  const stopMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  return (
    <PlateElement
      {...props}
      as="span"
      attributes={{
        ...props.attributes,
        contentEditable: false,
        [CHAT_EDITOR_DATA_ATTRS.mentionTypeName]: element.mentionTypeName,
        [CHAT_EDITOR_DATA_ATTRS.slateValue]: element.value,
      }}
    >
      <Box
        className={clsx(classes.mentionElement, selected && classes.mentionElementSelected)}
        component="span"
        onMouseDown={stopMouseDown}
      >
        {props.children}
        <Box component="span" sx={iconContainerStyle}>
          <Sparkle size={14} strokeWidth={2} />
        </Box>
        <Typography noWrap component="span">
          Aline AI
        </Typography>
        {!readOnly && (
          <span className={classes.chipRemoveButton} onClick={handleRemove}>
            <ChipRemoveIcon />
          </span>
        )}
      </Box>
    </PlateElement>
  );
});

export function MentionElement(
  props: PlateElementProps<TMentionElement> & {
    prefix?: string;
  }
): JSX.Element | null {
  const { element } = props;

  switch (element.mentionTypeName) {
    case 'aline-ai':
      return <AlineAiMentionChipElement props={props} />;
    case 'selection':
    case 'selection-v2':
    case 'selection-v3':
    case 'document':
      return <DocumentMentionElement element={element} props={props} />;
    case 'user':
      return <UserMentionChipElement element={element} props={props} />;
    case 'email':
      return <EmailMentionChipElement element={element} props={props} />;
    case 'intake-form':
      return <IntakeFormMentionChipElement element={element} props={props} />;
    case 'task':
      return <TaskMentionChipElement element={element} props={props} />;
    case 'workflow':
      return <WorkflowMentionChipElement element={element} props={props} />;
    case 'report':
      return <ReportMentionChipElement element={element} props={props} />;
    case 'playbook':
      return <PlaybookMentionChipElement element={element} props={props} />;
    case 'playbook-rule':
      return <PlaybookRuleMentionChipElement element={element} props={props} />;
    case 'report-column':
    case 'party': {
      /**
       * Report-column mentions are rendered by ReportColumnMentionElement
       * (reminder automation form). Party mentions still round-trip through
       * markdown serialize/deserialize so descriptions stay lossless; richer
       * chip rendering is deferred.
       */
      return (
        <PlateElement {...props} as="span">
          <Box component="span">{props.children}</Box>
        </PlateElement>
      );
    }
  }
}
